import axios from "axios";
import jwt_decode from "jwt-decode";
import appConfig from "configs/app.config";
import store from "../store";
import { onSignInSuccess, onSignOutSuccess, setIsUserEmailVerified } from "../store/auth/sessionSlice";
import { setUser } from "../store/auth/userSlice";
import { ACCESS_TOKEN_REFRESH_THRESHOLD_MS, IDLE_WARNING_MS } from "./constants";
import { authStore } from "./authStore";
import { SESSION_TYPE } from "./constants";
import {
  clearStoredAccessToken,
  readStoredAccessToken,
} from "./tokenStorage";

function isAdminToken(decoded) {
  const role = decoded?.extras?.role;
  return (
    decoded?.isAdmin === true ||
    role === "Admin" ||
    role === "SuperAdmin"
  );
}

function isTokenValid(token) {
  if (!token) {
    return false;
  }

  try {
    const decoded = jwt_decode(token);
    return decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

/**
 * Restore session from localStorage on reload (portal or admin).
 */
function tryHydrateFromLocalStorage() {
  const storedToken = readStoredAccessToken();

  if (!isTokenValid(storedToken)) {
    if (storedToken) {
      clearStoredAccessToken();
    }
    return false;
  }

  const decoded = jwt_decode(storedToken);
  const isAdmin = isAdminToken(decoded);

  authStore.setAuthState({
    accessToken: storedToken,
    sessionType: isAdmin ? SESSION_TYPE.ADMIN : SESSION_TYPE.PORTAL,
    usesRefreshCookie: !isAdmin,
  });

  store.dispatch(onSignInSuccess(storedToken));
  if (!isAdmin) {
    store.dispatch(setIsUserEmailVerified(decoded.isEmailVerified === true));
  }

  return true;
}

/**
 * Admin sessions do not use a refresh cookie — restore from persisted Redux
 * session after rehydrate when the JWT is still valid.
 */
function tryHydrateAdminFromPersistedSession() {
  const session = store.getState()?.auth?.session;
  const persistedToken = session?.token;

  if (!persistedToken || !session?.signedIn) {
    return false;
  }

  try {
    const decoded = jwt_decode(persistedToken);
    if (decoded.exp * 1000 <= Date.now()) {
      return false;
    }

    if (!isAdminToken(decoded)) {
      return false;
    }

    authStore.setAuthState({
      accessToken: persistedToken,
      sessionType: SESSION_TYPE.ADMIN,
      usesRefreshCookie: false,
    });

    return true;
  } catch {
    return false;
  }
}

const refreshClient = axios.create({
  baseURL: appConfig.api.domain,
  timeout: appConfig.api.timeout,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

let refreshInFlight = null;

function parseExpiresAt(token, explicitExpiresAt) {
  if (explicitExpiresAt) {
    return explicitExpiresAt;
  }
  try {
    const decoded = jwt_decode(token);
    if (decoded?.exp) {
      return new Date(decoded.exp * 1000).toISOString();
    }
  } catch {
    // ignore decode errors
  }
  return null;
}

function parseRefreshResponse(response) {
  const body = response?.data?.data ?? response?.data ?? {};
  const accessToken = body.access_token || body.accessToken || null;
  const accessTokenExpiresAt = parseExpiresAt(
    accessToken,
    body.accessTokenExpiresAt ||
      body.access_token_expires_at ||
      body.expiresAt ||
      null,
  );

  return { accessToken, accessTokenExpiresAt };
}

function isNearExpiry() {
  const { accessTokenExpiresAt, accessToken } = authStore.getState();
  if (!accessToken) {
    return true;
  }
  if (!accessTokenExpiresAt) {
    return true;
  }
  const expiresAt = new Date(accessTokenExpiresAt).getTime();
  if (Number.isNaN(expiresAt)) {
    return true;
  }
  return expiresAt - Date.now() < ACCESS_TOKEN_REFRESH_THRESHOLD_MS;
}

function applyRefreshResponse(response) {
  const { accessToken, accessTokenExpiresAt } = parseRefreshResponse(response);

  if (!accessToken) {
    throw new Error("Refresh response missing access_token");
  }

  authStore.setAuthState({
    accessToken,
    accessTokenExpiresAt,
    sessionType: SESSION_TYPE.PORTAL,
    usesRefreshCookie: true,
  });

  store.dispatch(onSignInSuccess(accessToken));
  try {
    const decoded = jwt_decode(accessToken);
    const emailVerified = decoded.isEmailVerified === true;
    store.dispatch(setIsUserEmailVerified(emailVerified));
    const currentUser = store.getState()?.auth?.user;
    if (currentUser?.isLogin) {
      store.dispatch(
        setUser({
          ...currentUser,
          isEmailVerified: emailVerified,
          isAccountApproved: decoded.isAccountApproved === true,
          authStatus: decoded.authStatus,
        }),
      );
    }
  } catch {
    store.dispatch(setIsUserEmailVerified(false));
  }

  return accessToken;
}

async function callRefreshEndpoint() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = refreshClient
    .post(appConfig.v2.refreshSession)
    .then(applyRefreshResponse)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * POST /v1/auth/refresh — requires httpOnly refresh cookie.
 * Portal sessions with an active refresh cookie only.
 */
export async function performRefresh() {
  if (!authStore.isPortalSession() || !authStore.usesRefreshCookie()) {
    return authStore.getAccessToken();
  }

  return callRefreshEndpoint();
}

function shouldExtendSession() {
  const { sessionExtendedAt } = authStore.getState();
  if (!sessionExtendedAt) {
    return true;
  }

  return Date.now() - sessionExtendedAt >= IDLE_WARNING_MS;
}

/**
 * Refresh while the user is active and JWT is near expiry or session should extend.
 */
export async function refreshWhileActive(source = "request") {
  const { isAuthenticated, pendingAccessToken } = authStore.getState();

  if (!isAuthenticated || pendingAccessToken) {
    return authStore.getAccessToken();
  }

  if (!authStore.isPortalSession() || !authStore.usesRefreshCookie()) {
    return authStore.getAccessToken();
  }

  if (!authStore.isUserActive()) {
    return authStore.getAccessToken();
  }

  if (!isNearExpiry() && !shouldExtendSession()) {
    return authStore.getAccessToken();
  }

  try {
    const token = await performRefresh();
    authStore.recordActivity();
    return token;
  } catch (error) {
    if (source === "bootstrap") {
      throw error;
    }
    return authStore.getAccessToken();
  }
}

/**
 * Proactively refresh when the access token is near expiry or session should extend.
 */
export async function refreshIfNearExpiry(source = "request") {
  return refreshWhileActive(source);
}

/**
 * Restore portal session on app load: localStorage first, then refresh cookie.
 */
export async function bootstrapSession() {
  authStore.setBootstrapping(true);

  let restoredFromStorage = false;

  try {
    restoredFromStorage = tryHydrateFromLocalStorage();

    try {
      await callRefreshEndpoint();
      return true;
    } catch {
      if (restoredFromStorage || tryHydrateFromLocalStorage()) {
        return true;
      }

      if (tryHydrateAdminFromPersistedSession()) {
        return true;
      }

      authStore.clearAuth();
      store.dispatch(onSignOutSuccess());
      return false;
    }
  } finally {
    authStore.setBootstrapping(false);
  }
}
